import { IAdvertisementResponse } from '@/api/services/advertisements/advertisementsService'
import { Button } from '@/components/ui/common/Button'
import { JSX, memo } from 'react'
import toast from 'react-hot-toast'

/**
 * Parses Google Font HTML and returns a styled span element with the font name
 * @param fontHtml - HTML containing Google Font link tags
 */
const parseFontDisplay = (fontHtml: string): JSX.Element | string => {
  try {
    // Extract font family from URL
    const match = /family=([^&:]+)/.exec(fontHtml)

    if (!match?.[1]) return 'Brak czcionki'

    // Get the font name and replace '+' with spaces
    const fontName = match[1].replace(/\+/g, ' ')

    return <span style={{ fontFamily: `${fontName}, sans-serif` }}>{fontName}</span>
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_) {
    return 'Brak czcionki'
  }
}

const DescriptionBlock = ({
  label,
  value
}: {
  label: string
  value: JSX.Element | string
}) => {
  return (
    <div className="flex w-full flex-col gap-2">
      <span className="text-lg font-medium">{label}</span>
      <div className="rounded-md border-border/60 px-2 py-1 md:border">
        <span className="text-muted-foreground text-base">{value}</span>
      </div>
    </div>
  )
}

function AdDetails({ data }: { data: IAdvertisementResponse | undefined }) {
  if (!data)
    return <div className="text-center text-lg">Wystąpił problem z ładowaniem danych</div>

  return (
    <div className="flex h-full w-full flex-col">
      {/*  */}
      <div className="flex w-full flex-col justify-end gap-2">
        <div className="flex min-h-92 items-center justify-center rounded-md border border-border">
          {data?.resultImageFileId ? (
            <img
              alt="Wygenerowana reklama"
              className="max-h-[680px] rounded-md object-contain"
              src={`${import.meta.env.VITE_API_URL}/api/v1/files/${data?.resultImageFileId}`}
            />
          ) : (
            'Brak zdjęcia'
          )}
        </div>
        <Button
          onClick={async () => {
            if (data?.resultImageFileId) {
              const imageUrl = `${import.meta.env.VITE_API_URL}/api/v1/files/${data.resultImageFileId}`
              const filename = `advertisement-${data.id}.jpg` // Assuming JPG, adjust if necessary

              try {
                // Fetch the image data
                const response = await fetch(imageUrl)
                if (!response.ok) {
                  console.error(`Failed to download image. Status: ${response.status}`)
                  toast.error('Pobieranie się nie powiodło, spróbuj ponownie.')
                  return
                }

                const blob = await response.blob()

                const objectUrl = URL.createObjectURL(blob)

                const link = document.createElement('a')
                link.href = objectUrl
                link.download = filename // This attribute prompts the browser to download the linked URL

                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)

                URL.revokeObjectURL(objectUrl)
              } catch (error) {
                console.error('Error downloading image:', error)
                toast.error('Wystąpił błąd podczas pobierania obrazu.')
              }
            }
          }}
        >
          Pobierz zdjęcie
        </Button>
      </div>
      {/*  */}
      <div className="mt-10 flex w-full flex-col items-center justify-between md:flex-row">
        <div className="flex max-w-xs flex-1 flex-col items-center gap-5">
          <div className="flex h-24 w-full items-center justify-center rounded-md border-border md:h-48 md:border">
            <img
              alt="Logo producenta"
              className="h-full rounded-md object-contain"
              src={`${import.meta.env.VITE_API_URL}/api/v1/files/${data.job.shopLogoFileId}`}
            />
          </div>
          <span className="text-lg font-medium">Logo producenta</span>
        </div>

        <div className="flex max-w-xs flex-1 flex-col items-center gap-5">
          <div className="flex h-24 w-full items-center justify-center rounded-md border-border md:h-48 md:border">
            <img
              alt="Zdjęcie produktu"
              className="h-full rounded-md object-contain"
              src={`${import.meta.env.VITE_API_URL}/api/v1/files/${data.job.productImageFileId}`}
            />
          </div>
          <span className="text-lg font-medium">Zdjęcie produktu</span>
        </div>
      </div>
      {/*  */}
      <div className="mt-10 flex w-full flex-col gap-8">
        <DescriptionBlock
          label="Opis kreacji"
          value={data.job.creationDescription || 'Brak opisu kreacji'}
        />
        <DescriptionBlock
          label="Opis kolorów"
          value={data.job.mainColors || 'Brak opisu kolorów'}
        />
        <div className="grid grid-cols-2 gap-8">
          <DescriptionBlock label="Cena" value={data.job.price || 'Brak ceny'} />

          <DescriptionBlock
            label="Opis do ceny"
            value={data.job.price || 'Brak opisu ceny'}
          />
          <DescriptionBlock
            label="Nazwa produktu"
            value={data.job.productName || 'Brak nazwy produktu'}
          />

          <DescriptionBlock
            label="Czcionka"
            value={
              data.job.preferredFontHeadHtml
                ? parseFontDisplay(data.job.preferredFontHeadHtml)
                : 'Brak czcionki'
            }
          />
        </div>
        <DescriptionBlock
          label="Nagłówek reklamy"
          value={data.job.adSlogan || 'Brak nagłówka reklamy'}
        />
        <DescriptionBlock
          label="Cena sprzed 30 dni"
          value={data.job.priceLast30Days || 'Brak ceny sprzed 30 dni'}
        />
        <DescriptionBlock
          label="Rozdzielczość"
          value={data.job.quality || 'Brak podanej rozdzielczości'}
        />
      </div>
    </div>
  )
}

export default memo(AdDetails)
